import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "@/components/layout/AdminLayout";
import API, { formatApiError, formatPrice, getImageUrl } from "@/lib/api";
import { useLanguage } from "@/contexts/LanguageContext";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowUpRight, ChevronLeft, ChevronRight, Download, MapPin, Phone, RefreshCcw, Search, ShoppingBag, User } from "lucide-react";

const itemsPerPage = 10;
const roleOptions = [
  { value: "all", label: "Semua Kategori" },
  { value: "retail", label: "Retail" },
  { value: "reseller", label: "Reseller" },
  { value: "agen", label: "Agen" },
  { value: "mitra", label: "Mitra" },
];
const statusOptions = [
  { value: "all", label: "Semua Status" },
  { value: "active", label: "Aktif" },
  { value: "inactive", label: "Tidak Aktif" },
];

const roleBadgeClass = {
  retail: "bg-stone-100 text-stone-700",
  reseller: "bg-blue-100 text-blue-700",
  agen: "bg-amber-100 text-amber-700",
  mitra: "bg-purple-100 text-purple-700",
  produsen: "bg-purple-100 text-purple-700",
};

const statusBadgeClass = {
  active: "bg-emerald-100 text-emerald-700",
  inactive: "bg-stone-100 text-stone-600",
};

function formatRole(role) {
  if (role === "retail") return "Retail";
  if (role === "reseller") return "Reseller";
  if (role === "agen") return "Agen";
  if (role === "mitra" || role === "produsen") return "Mitra";
  return role || "-";
}

function formatStatus(status) {
  if (status === "active") return "Aktif";
  if (status === "inactive") return "Tidak Aktif";
  return status || "-";
}

function getFilenameFromDisposition(disposition, fallback) {
  const match = disposition?.match(/filename="?([^";]+)"?/i);
  return match?.[1] || fallback;
}

export default function AdminCustomers() {
  const navigate = useNavigate();
  const { t } = useLanguage();

  const [customers, setCustomers] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exportingId, setExportingId] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, per_page: itemsPerPage, total: 0, total_pages: 1 });

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
    }, 300);
    return () => window.clearTimeout(timer);
  }, [searchQuery]);

  const fetchCustomers = async (page = currentPage) => {
    setLoading(true);
    try {
      const params = {
        page,
        per_page: itemsPerPage,
      };
      if (debouncedSearch) params.search = debouncedSearch;
      if (roleFilter !== "all") params.role = roleFilter;
      if (statusFilter !== "all") params.status = statusFilter;

      const response = await API.get("/admin/customer-directory", { params });
      setCustomers(response.data?.users || []);
      setPagination(response.data?.pagination || { page, per_page: itemsPerPage, total: 0, total_pages: 1 });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, roleFilter, statusFilter]);

  useEffect(() => {
    fetchCustomers(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, debouncedSearch, roleFilter, statusFilter]);

  useEffect(() => {
    setSelectedIds([]);
  }, [customers]);

  const allSelected = useMemo(() => customers.length > 0 && customers.every((item) => selectedIds.includes(item.user_id)), [customers, selectedIds]);

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds([]);
      return;
    }
    setSelectedIds(customers.map((item) => item.user_id));
  };

  const toggleSelectOne = (userId) => {
    setSelectedIds((prev) => prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]);
  };

  const handleExport = async (event, customer) => {
    event.stopPropagation();
    setExportingId(customer.user_id);
    try {
      const response = await API.get(`/admin/customer-directory/${customer.user_id}/orders/export`, {
        responseType: "blob",
      });
      const filename = getFilenameFromDisposition(
        response.headers["content-disposition"],
        `customer-orders-${(customer.name || customer.user_id).replace(/\s+/g, "-").toLowerCase()}.csv`
      );
      const blob = new Blob([response.data], { type: "text/csv;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Order customer berhasil diekspor");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail || err.message));
    } finally {
      setExportingId("");
    }
  };

  return (
    <AdminLayout title={t("admin_customers.title")}>
      <div data-testid="admin-customers" className="space-y-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <p className="text-sm text-stone-500">
              Direktori customer operasional dari akun yang sudah bertransaksi.
            </p>
            <p className="text-xs text-stone-400 mt-1">
              Total {pagination.total || 0} customer, data order dibaca aman dari transaksi existing.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
            <div className="relative w-full sm:w-72">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-4 w-4 text-stone-400" />
              </div>
              <Input
                type="text"
                placeholder={t("common.search")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 h-9"
              />
            </div>
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="w-full sm:w-[160px] h-9 bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {roleOptions.map((item) => (
                  <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-[160px] h-9 bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {statusOptions.map((item) => (
                  <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <button
              onClick={() => fetchCustomers(1)}
              className="h-9 px-3 border border-stone-200 rounded-md text-stone-600 hover:bg-stone-50 transition-colors inline-flex items-center justify-center gap-2"
            >
              <RefreshCcw className="w-4 h-4" />
              Refresh
            </button>
          </div>
        </div>

        <div className="bg-white border border-stone-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-stone-100 bg-stone-50/50">
                  <th className="p-4 w-12">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleSelectAll}
                      className="rounded border-stone-300"
                      aria-label="Pilih semua customer"
                    />
                  </th>
                  <th className="text-left p-4 text-xs font-semibold tracking-wider uppercase text-stone-500">Pelanggan</th>
                  <th className="text-left p-4 text-xs font-semibold tracking-wider uppercase text-stone-500">Kategori</th>
                  <th className="text-left p-4 text-xs font-semibold tracking-wider uppercase text-stone-500">Telepon</th>
                  <th className="text-left p-4 text-xs font-semibold tracking-wider uppercase text-stone-500">Alamat</th>
                  <th className="text-left p-4 text-xs font-semibold tracking-wider uppercase text-stone-500">Ringkasan</th>
                  <th className="text-right p-4 text-xs font-semibold tracking-wider uppercase text-stone-500">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="p-8 text-center text-stone-400">{t("common.loading")}</td>
                  </tr>
                ) : customers.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="p-8 text-center text-stone-400 italic">Belum ada customer operasional yang cocok dengan filter ini.</td>
                  </tr>
                ) : customers.map((customer) => (
                  <tr
                    key={customer.user_id}
                    className="border-b border-stone-50 hover:bg-stone-50/70 transition-colors cursor-pointer"
                    onClick={() => navigate(`/admin/customers/${customer.user_id}`)}
                  >
                    <td className="p-4" onClick={(event) => event.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(customer.user_id)}
                        onChange={() => toggleSelectOne(customer.user_id)}
                        className="rounded border-stone-300"
                        aria-label={`Pilih ${customer.name}`}
                      />
                    </td>
                    <td className="p-4 min-w-[240px]">
                      <div className="flex items-center gap-3">
                        {customer.picture ? (
                          <img src={getImageUrl(customer.picture)} alt={customer.name} className="w-10 h-10 rounded-full object-cover shrink-0" />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-stone-100 flex items-center justify-center shrink-0">
                            <User className="w-4 h-4 text-stone-400" />
                          </div>
                        )}
                        <div className="min-w-0">
                          <p className="font-medium text-stone-900 truncate">{customer.name || "Tanpa Nama"}</p>
                          <p className="text-xs text-stone-500 truncate">{customer.email || "-"}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-4">
                      <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded inline-flex ${roleBadgeClass[customer.role] || "bg-stone-100 text-stone-700"}`}>
                        {formatRole(customer.role)}
                      </span>
                      <div className="mt-2">
                        <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded inline-flex ${statusBadgeClass[customer.status] || "bg-stone-100 text-stone-600"}`}>
                          {formatStatus(customer.status)}
                        </span>
                      </div>
                    </td>
                    <td className="p-4 text-stone-600">
                      <span className="inline-flex items-center gap-2 text-xs">
                        <Phone className="w-3 h-3 text-stone-400" />
                        {customer.phone || "-"}
                      </span>
                    </td>
                    <td className="p-4 max-w-[240px] text-stone-600">
                      <span className="inline-flex items-start gap-2 text-xs leading-relaxed">
                        <MapPin className="w-3 h-3 text-stone-400 mt-0.5 shrink-0" />
                        <span className="line-clamp-2">{customer.address || "-"}</span>
                      </span>
                    </td>
                    <td className="p-4 min-w-[160px]">
                      <div className="flex flex-col gap-1 text-xs text-stone-600">
                        <span className="inline-flex items-center gap-2"><ShoppingBag className="w-3 h-3 text-stone-400" /> {customer.total_orders || 0} order</span>
                        <span>Total belanja {formatPrice(customer.total_spent || 0)}</span>
                      </div>
                    </td>
                    <td className="p-4 text-right" onClick={(event) => event.stopPropagation()}>
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => navigate(`/admin/customers/${customer.user_id}`)}
                          className="inline-flex items-center gap-1.5 px-3 h-8 rounded-md border border-stone-200 text-xs text-stone-600 hover:bg-stone-50 transition-colors"
                        >
                          <ArrowUpRight className="w-3.5 h-3.5" />
                          Detail
                        </button>
                        <button
                          onClick={(event) => handleExport(event, customer)}
                          disabled={exportingId === customer.user_id}
                          className="inline-flex items-center gap-1.5 px-3 h-8 rounded-md bg-[#8A9A86] text-white text-xs hover:bg-[#768573] transition-colors disabled:opacity-60"
                        >
                          <Download className="w-3.5 h-3.5" />
                          {exportingId === customer.user_id ? "Ekspor..." : "Ekspor"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {pagination.total_pages > 1 && (
          <div className="flex items-center justify-between pt-2">
            <p className="text-sm text-stone-500">
              {t("common.page")} {pagination.page} {t("common.of")} {pagination.total_pages}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                disabled={pagination.page <= 1}
                className="p-1.5 border border-stone-200 rounded text-stone-500 hover:bg-stone-50 disabled:opacity-50"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => setCurrentPage((page) => Math.min(pagination.total_pages, page + 1))}
                disabled={pagination.page >= pagination.total_pages}
                className="p-1.5 border border-stone-200 rounded text-stone-500 hover:bg-stone-50 disabled:opacity-50"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
